import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import styles from './CreateSecurityRole.module.scss';
import {
	getPermissions,
	Permission,
	PERMISSION_FUNCTIONS,
} from '../../../../../core/API/permissions';
import {
	getSecurityRoleTypes,
	SecurityRoleType,
} from '../../../../../core/API/securityRoles';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { SecurityRoleMatrix } from './SecurityRoleMatrix';
import { ModulePermission } from './SecurityRoleMatrix.types';
import { Button } from '../../../../../components/ui/button';
import {
	Grid,
	TextField,
	FormControl,
	FormLabel,
	RadioGroup,
	FormControlLabel,
	Radio,
	Select,
	MenuItem,
	InputLabel,
	Autocomplete,
} from '@mui/material';
import { Box, Typography } from '@mui/material';

interface CreateSecurityRoleProps {
	backButtonClick: () => void;
	onCreateRole?: (roleData: CreateSecurityRoleRequestData) => void;
	isLoading?: boolean;
	error?: string | null;
}

interface CreateRolePermission {
	permission: string;
	view?: boolean;
	edit?: boolean;
	delete?: boolean;
	execute?: boolean;
	securityRole?: string;
}

interface CreateSecurityRoleRequestData {
	name: string;
	description: string;
	workplace?: string;
	securityRoleType?: string;
	securityRolePermission: CreateRolePermission[];
}

interface CreateSecurityRoleFormData {
	name: string;
	description: string;
	role_owner: 'workplace' | 'organization';
	securityRoleType: string;
	permissions: ModulePermission[];
}

// Permissions fetched from API

// Validation schema using Yup
const validationSchema = Yup.object().shape({
	name: Yup.string()
		.required('Role name is required')
		.min(2, 'Role name must be at least 2 characters')
		.max(30, 'Role name must be less than 50 characters')
		.matches(
			/^[a-zA-Z0-9\s_-]+$/,
			'Role name can only contain letters, numbers, spaces, hyphens, and underscores'
		),
	description: Yup.string(),
	role_owner: Yup.string()
		.oneOf(['workplace', 'organization', 'system'], 'Please select a valid role owner')
		.required('Role owner is required'),
	securityRoleType: Yup.string().required('Notifications Level for Role is required'),
	permissions: Yup.array()
		.of(
			Yup.object().shape({
				module: Yup.string().required(),
				functions: Yup.array()
					.of(
						Yup.object().shape({
							name: Yup.string().required(),
							permissions: Yup.object().required(),
						})
					)
					.required(),
			})
		)
		.required('Permissions must be configured'),
});

const getPermissionMatrix = () => {
	const permissionMatrix: Record<string, boolean> = {};
	for (const key of PERMISSION_FUNCTIONS) {
		permissionMatrix[key] = false;
	}
	return permissionMatrix;
};

const mapPermissionsToModulePermissions = (permissions: Permission[]): ModulePermission[] => {
	const groupedPermissions = _.groupBy(permissions, 'permissionGroup.label');
	return Object.keys(groupedPermissions).map((module) => ({
		module,
		functions: groupedPermissions[module].map((func) => ({
			id: func.id,
			name: func.label,
			permissions: getPermissionMatrix(),
		})),
	}));
};

const CreateSecurityRole: React.FC<CreateSecurityRoleProps> = ({
	backButtonClick,
	onCreateRole,
	isLoading = false,
	error = null,
}) => {
	const [allowedPermissions, setAllowedPermissions] = useState<Permission[]>([]);
	const [securityRoleTypes, setSecurityRoleTypes] = useState<SecurityRoleType[]>([]);
	const [loadingPermissions, setLoadingPermissions] = useState<boolean>(true);
	const initialValues: CreateSecurityRoleFormData = useMemo(() => ({
		name: '',
		description: '',
		role_owner: 'workplace',
		securityRoleType: '',
		permissions: mapPermissionsToModulePermissions(allowedPermissions),
	}), [allowedPermissions]);

	// Scroll to top when component mounts
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		const fetchAllowedPermissions = async () => {
			setLoadingPermissions(true);
			try {
				const [permissionsData, roleTypesData] = await Promise.all([
					getPermissions(),
					getSecurityRoleTypes(),
				]);
				// If API returns items, use them; otherwise fallback to []
				setAllowedPermissions(permissionsData.items || []);
				setSecurityRoleTypes(roleTypesData || []);
			} catch (error) {
				setAllowedPermissions([]);
				setSecurityRoleTypes([]);
			}
			setLoadingPermissions(false);
		};
		fetchAllowedPermissions();
	}, []);

	// Helper function to check if any permissions are selected
	const hasAnyPermissions = (permissions: ModulePermission[]): boolean => {
		return permissions.some((module) =>
			module.functions.some((func) =>
				Object.values(func.permissions).some((permission) => permission === true)
			)
		);
	};

	const handleSubmit = (values: CreateSecurityRoleFormData) => {
		// Check if at least one permission is selected
		if (!hasAnyPermissions(values.permissions)) {
			alert('Please select at least one permission for this role.');
			return;
		}

		// console.log('Creating security role with data:', values);
		const allPermissions = _.flatten(values.permissions.map(perm=>perm.functions))
		const roleData: CreateSecurityRoleRequestData = {
			name: values.name,
			description: values.description,
			workplace: null,
			securityRoleType: values.securityRoleType || undefined,
			securityRolePermission: allPermissions.map(perm => ({
					permission: `/api/permissions/${perm.id}`,
					...perm.permissions
				}))
		};
		if (formik.values.role_owner === 'workplace') {
			const workplaceId = localStorage.getItem("workplace-id");
			roleData.workplace = `/api/workplaces/${workplaceId}`;
		}
		if (onCreateRole) {
			onCreateRole(roleData);
		}
		// Note: Don't call backButtonClick() here - let the parent handle navigation after success
	};

	const formik = useFormik({
		initialValues,
		enableReinitialize: true,
		validationSchema,
		onSubmit: handleSubmit,
	});
	// console.log('formik values', formik.values);

	// Count selected permissions
	const selectedPermissionsCount = formik.values.permissions.reduce((total, module) => {
		return (
			total +
			module.functions.reduce((moduleTotal, func) => {
				return moduleTotal + Object.values(func.permissions).filter((p) => p === true).length;
			}, 0)
		);
	}, 0);

	const handleSaveClick = () => {
		formik.handleSubmit();
	};

	const isSubmitting = formik.isSubmitting || isLoading;

	if (loadingPermissions) {
		return (
			<div style={{ padding: '32px', textAlign: 'center', fontSize: '18px' }}>
				Loading permissions...
			</div>
		);
	}

	return (
		<Box
			sx={{
				background: 'white',
				borderRadius: 2,
				boxShadow: 2,
				p: 3,
				maxWidth: '900px',
				margin: '0 auto',
			}}
		>
			<Grid container direction="row" justifyContent="space-between">
				<Grid>
					<Button className={styles.saveButton} onClick={backButtonClick}>
						Back
					</Button>
				</Grid>
				<Grid item xs={6} display={'flex'} justifyContent="flex-end">
					<Grid>
						<Button
							className={styles.saveButton}
							onClick={handleSaveClick}
							disabled={isSubmitting}
						>
							{isSubmitting ? 'Saving...' : 'Save'}
						</Button>
					</Grid>
				</Grid>
			</Grid>
			<Typography variant="h5" sx={{ my: 2, fontWeight: 600 }}>
				Create Security Role
			</Typography>

			{error && (
				<Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
					<Typography color="error" variant="body2">
						{error}
					</Typography>
				</Box>
			)}

			<form onSubmit={formik.handleSubmit}>
				<Box sx={{ mb: 3 }}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TextField
								name="name"
								label="Role Name"
								fullWidth
								required
								variant="outlined"
								margin="normal"
								value={formik.values.name}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								error={formik.touched.name && Boolean(formik.errors.name)}
								helperText={formik.touched.name && formik.errors.name}
							/>
						</Grid>
						<Grid item xs={12}>
							<FormControl component="fieldset" margin="normal" fullWidth>
								<FormLabel component="legend">Role Owner</FormLabel>
								<RadioGroup
									row
									name="role_owner"
									value={formik.values.role_owner}
									onChange={formik.handleChange}
								>
									<FormControlLabel value="workplace" control={<Radio />} label="Workplace" />
									<FormControlLabel value="organization" control={<Radio />} label="Organization" />
								</RadioGroup>
								{formik.touched.role_owner && formik.errors.role_owner && (
									<Typography color="error" variant="caption">
										{formik.errors.role_owner}
									</Typography>
								)}
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<Autocomplete
								options={[...securityRoleTypes].sort((a, b) => 
									a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
								)}
								getOptionLabel={(option) => {
									if (typeof option === 'string') return option;
									return option.label || '';
								}}
								value={securityRoleTypes.find((type) => type?.['@id'] === formik.values.securityRoleType) || null}
								onChange={(_, value) => formik.setFieldValue('securityRoleType', value ? value?.['@id'] : '')}
								isOptionEqualToValue={(option, value) => option?.['@id'] === value?.['@id']}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Notifications Level for Role"
										name="securityRoleType"
										margin="normal"									required										error={formik.touched.securityRoleType && Boolean(formik.errors.securityRoleType)}
										helperText={formik.touched.securityRoleType && formik.errors.securityRoleType}
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								name="description"
								label="Description"
								fullWidth
								multiline
								rows={3}
								variant="outlined"
								margin="normal"
								value={formik.values.description}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								error={formik.touched.description && Boolean(formik.errors.description)}
								helperText={formik.touched.description && formik.errors.description}
							/>
						</Grid>
					</Grid>
				</Box>

				<Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
					<Typography variant="h6" sx={{ mb: 1 }}>
						Role Permissions
					</Typography>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Configure the permissions for this security role. Selected permissions:{' '}
						<strong>{selectedPermissionsCount}</strong>
					</Typography>
					<Grid>
						<SecurityRoleMatrix
							data={formik.values.permissions}
							onChange={(updatedPermissions) => {
								formik.setFieldValue('permissions', updatedPermissions);
							}}
						/>
					</Grid>
				</Box>
			</form>
		</Box>
	);
};

export default CreateSecurityRole;
